import { parsePhoneNumberFromString } from "libphonenumber-js";
import * as Yup from "yup";

import { EMAIL_REGEX } from "../constants/common";

export const validatePhoneNumber = (phone, countryCode, t) => {
  if (!phone) return t("validation.required");

  const parsedNumber = parsePhoneNumberFromString(`+${phone}`, countryCode);
  if (!parsedNumber || !parsedNumber.isValid()) {
    return t("validation.phone_invalid");
  }
  return null; // No error
};

// loginValidationSchema
export const loginValidationSchema = (t) => {
  return Yup.object({
    username: Yup.string().required(t("validation.required")),
    password: Yup.string().required(t("validation.required")),
  });
};

// AddStudentModalValidationSchema
export const addStudentModalValidationSchema = (t, isThirdPartyUser) => {
  return Yup.object().shape({
    name: Yup.string()
      .min(2, t("validation.field_min_2"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),

    ...(isThirdPartyUser
      ? {
          email: Yup.string()
            .matches(EMAIL_REGEX, t("validation.invalid_email"))
            .required(t("validation.required")),
        }
      : {
          username: Yup.string()
            .matches(/^\S*$/, t("validation.username_invalid"))
            .min(3, t("validation.field_min_3"))
            .max(20, t("validation.field_max_20"))
            .required(t("validation.required")),
          password: Yup.string()
            .min(6, t("validation.password_at_least_6", { count: 6 }))
            .max(32, t("validation.field_max_20"))
            .required(t("validation.required")),
          confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], t("validation.passwords_must_match"))
            .required(t("validation.required")),
        }),
    image: Yup.mixed().nullable(),
  });
};

export const addTeacherModalValidationSchema = (t, isThirdPartyUser) => {
  return Yup.object().shape({
    name: Yup.string()
      .min(2, t("validation.field_min_2"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),

    // Normal user
    ...(isThirdPartyUser
      ? {
          b12_email: Yup.string()
            .matches(EMAIL_REGEX, t("validation.invalid_email"))
            .required(t("validation.required")),
        }
      : {
          username: Yup.string()
            .matches(/^\S*$/, t("validation.username_invalid"))
            .min(3, t("validation.field_min_3"))
            .max(20, t("validation.field_max_20"))
            .required(t("validation.required")),

          email: Yup.string()
            .matches(EMAIL_REGEX, t("validation.invalid_email"))
            .required(t("validation.required")),
          password: Yup.string()
            .min(8, t("validation.password_at_least_8", { count: 8 }))
            .max(20, t("validation.field_max_20"))
            .required(t("validation.required")),

          confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], t("validation.passwords_must_match"))
            .required(t("validation.required")),
        }),
  });
};

export const addClassroomModalValidationSchema = (t) => {
  return Yup.object({
    classroomName: Yup.string()
      .min(2, t("validation.field_min_2"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),
    section: Yup.string()
      .min(1, t("validation.field_min_1"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),
    teacher: Yup.string().required(t("validation.required")),
    grade: Yup.string().required(t("validation.required")),
    course: Yup.string().required(t("validation.required")),
  });
};

export const assignMultiStudentsModalValidationSchema = (t) => {
  return Yup.object({
    selectedStudents: Yup.array()
      .of(Yup.string()) // Ensures the array contains strings
      .min(1, t("validation.required")), // Requires at least one selection
  });
};

export const resetChallengeModalValidationSchema = (t, isForChildren) =>
  Yup.object().shape({
    selectedChallenge: Yup.string().required(t("validation.required")),
    selectedSubchallenge: isForChildren
      ? Yup.mixed().notRequired()
      : Yup.array().of(Yup.string()).required(t("validation.required")),
  });

export const resetQuestionModalValidationSchema = (t) => {
  return Yup.object({
    selectedLessonQuestion: Yup.string().required(t("validation.required")), // Requires a non-empty string
  });
};

export const assignStudentModalValidationSchema = (t) => {
  return Yup.object({
    classroom: Yup.string().required(t("validation.required")), // Ensure a non-empty string
  });
};

export const editClassroomModalValidationSchema = (t) => {
  return Yup.object({
    name: Yup.string()
      .min(2, t("validation.field_min_2"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),
    grade: Yup.mixed().required(t("validation.required")),
    section: Yup.string()
      .min(1, t("validation.field_min_1"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),
  });
};

export const reassignModalValidationSchema = (t) => {
  return Yup.object({
    teacher: Yup.string().required(t("validation.required")),
  });
};

export const editLoggedUserModalValidationSchema = (t, showChangePassword) => {
  return Yup.object({
    name: Yup.string()
      .min(2, t("validation.field_min_2"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),

    username: Yup.string()
      .matches(/^\S*$/, t("validation.username_invalid"))
      .min(3, t("validation.field_min_3"))
      .max(20, t("validation.field_max_20"))
      .required(t("validation.required")),

    email: Yup.string()
      .matches(EMAIL_REGEX, t("validation.invalid_email"))
      .required(t("validation.required")),

    password: showChangePassword
      ? Yup.string()
          .min(8, t("validation.password_at_least_8", { count: 8 }))
          .max(20, t("validation.field_max_20"))
          .required(t("validation.required"))
      : Yup.string(),

    confirmPassword: showChangePassword
      ? Yup.string()
          .oneOf([Yup.ref("password")], t("validation.passwords_must_match"))
          .required(t("validation.required"))
      : Yup.string(),
  });
};

export const editTeacherModalValidationSchema = (t, showChangePassword, isThirdPartyUser) => {
  return Yup.object({
    name: Yup.string()
      .min(2, t("validation.field_min_2"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),

    ...(isThirdPartyUser
      ? {
          email: Yup.string()
            .matches(EMAIL_REGEX, t("validation.invalid_email"))
            .required(t("validation.required")),
        }
      : {
          email: Yup.string()
            .matches(EMAIL_REGEX, t("validation.invalid_email"))
            .required(t("validation.required")),

          username: Yup.string()
            .matches(/^\S*$/, t("validation.username_invalid"))
            .min(3, t("validation.field_min_3"))
            .max(20, t("validation.field_max_20"))
            .required(t("validation.required")),
          password: showChangePassword
            ? Yup.string()
                .min(8, t("validation.password_at_least_8", { count: 8 }))
                .max(20, t("validation.field_max_20"))
                .required(t("validation.required"))
            : Yup.string(),

          confirmPassword: showChangePassword
            ? Yup.string()
                .oneOf([Yup.ref("password")], t("validation.passwords_must_match"))
                .required(t("validation.required"))
            : Yup.string(),
        }),
  });
};

export const editStudentModalValidationSchema = (t, showChangePassword, isThirdPartyUser) => {
  return Yup.object({
    name: Yup.string()
      .min(2, t("validation.field_min_2"))
      .max(25, t("validation.field_max_25"))
      .required(t("validation.required")),

    ...(isThirdPartyUser
      ? {
          email: Yup.string()
            .matches(EMAIL_REGEX, t("validation.invalid_email"))
            .required(t("validation.required")),
        }
      : {
          username: Yup.string()
            .matches(/^\S*$/, t("validation.username_invalid"))
            .min(3, t("validation.field_min_3"))
            .max(20, t("validation.field_max_20"))
            .required(t("validation.required")),
          password: showChangePassword
            ? Yup.string()
                .min(6, t("validation.password_at_least_6", { count: 6 }))
                .max(20, t("validation.field_max_20"))
                .required(t("validation.required"))
            : Yup.string(),
          confirmPassword: showChangePassword
            ? Yup.string()
                .oneOf([Yup.ref("password")], t("validation.passwords_must_match"))
                .required(t("validation.required"))
            : Yup.string(),
        }),
  });
};
